import { plainName } from "../../lib/program-names";
import type { WidgetActions } from "../use-widget-actions";
import type { CogentWidgetProps } from "../widget-types";
import s from "../cogent.module.css";

function JoinAction({
  name,
  props,
  busy,
}: {
  name: string;
  props: Pick<CogentWidgetProps, "start" | "coworld">;
  busy: boolean;
}) {
  const { start, coworld } = props;

  if (start?.onPage) return null;

  if (start?.label && start.onAction) {
    return (
      <div className={s.row}>
        <button
          type="button"
          className={`${s.secondary} ${s.wrap}`}
          onClick={start.onAction}
          disabled={start.busy || busy}
          data-testid="cogent-join"
        >
          {start.label}
        </button>
      </div>
    );
  }

  if (coworld.joinHref) {
    return (
      <div className={s.row}>
        <a
          className={`${s.secondary} ${s.wrap}`}
          href={coworld.joinHref}
          data-testid="cogent-join"
        >
          Bring {name} here
        </a>
      </div>
    );
  }

  return start?.text ? (
    <p className={s.soft} data-testid="cogent-setup-line">
      {start.text}
    </p>
  ) : null;
}

export function WidgetParticipation({
  name,
  props,
  actions,
}: {
  name: string;
  props: Pick<
    CogentWidgetProps,
    "coworld" | "start" | "canRelease" | "releasable"
  >;
  actions: WidgetActions;
}) {
  const { releasable } = props;

  if (props.coworld.joinHref || !props.canRelease)
    return <JoinAction name={name} props={props} busy={actions.busy} />;

  if (!actions.releasing) {
    return (
      <div className={s.row}>
        <button
          type="button"
          className={s.secondary}
          onClick={() => actions.setReleasing(true)}
          disabled={actions.busy}
          data-testid="cogent-release"
        >
          Release a new version
        </button>
      </div>
    );
  }

  return (
    <div className={s.release}>
      {releasable.length === 0 ? (
        <p className={s.soft}>
          Every version you have is already released here.
        </p>
      ) : (
        <select
          className={s.field}
          aria-label="Version to release"
          value={actions.versionId}
          onChange={(event) => actions.setVersionId(event.target.value)}
        >
          {releasable.map((version) => (
            <option key={version.id} value={version.id}>
              {plainName(version.label)}
            </option>
          ))}
        </select>
      )}
      <div className={s.row}>
        {releasable.length > 0 ? (
          <button
            type="button"
            className={s.secondary}
            onClick={actions.release}
            disabled={actions.busy}
          >{`Ask ${name} to release`}</button>
        ) : null}
        <button
          type="button"
          className={s.ghost}
          onClick={() => actions.setReleasing(false)}
          disabled={actions.busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
